/**
 * Yarn-shop module page (/garnbutikk).
 *
 * Audits PDF coverage on patterns/kits and surfaces:
 *   1. Patterns/PDF-category products missing a PDF download
 *   2. Downloadable products with empty `downloads`
 *   3. Patterns with PDF but missing `virtual` flag (bulk-fixable)
 *   4. Orphan PDFs in media library — with auto-match suggestions and a
 *      free-text product search to attach manually.
 *
 * Locked behind `user.shop_type === 'yarn'` (see Sidebar.js).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  FilePdf, Link as LinkIcon, MagicWand, MagnifyingGlass, Warning, Sparkle, ArrowSquareOut, Plug,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => (n == null ? '–' : new Intl.NumberFormat('nb-NO').format(n));

const SummaryCard = ({ label, value, intent = 'neutral', icon: Icon, hint }) => {
  const intents = {
    danger: 'border-red-500/40 bg-red-500/5',
    warn: 'border-amber-500/40 bg-amber-500/5',
    neutral: 'border-border bg-card',
    good: 'border-emerald-500/40 bg-emerald-500/5',
  };
  return (
    <Card className={`${intents[intent]} border`} data-testid={`yarn-summary-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4 flex flex-col gap-1">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
          {Icon && <Icon size={14} weight="bold" />}
          {label}
        </div>
        <div className="text-3xl font-heading font-semibold">{fmt(value)}</div>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  );
};

const ProductRow = ({ product, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 py-2 border-b border-border/60 last:border-0" data-testid={`yarn-product-row-${product.id}`}>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <span className="font-medium truncate" title={product.name}>{product.name}</span>
        {product.permalink && (
          <a
            href={product.permalink}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-primary shrink-0"
            title="Åpne i WooCommerce"
          >
            <ArrowSquareOut size={12} />
          </a>
        )}
      </div>
      <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
        <span>SKU: {product.sku || '–'}</span>
        <span className="hidden sm:inline">·</span>
        <span className="truncate max-w-[60vw] sm:max-w-none">{(product.categories || []).join(', ') || 'ingen kategori'}</span>
        {product.downloadable && <Badge variant="outline" className="text-[10px]">downloadable</Badge>}
        {product.virtual && <Badge variant="outline" className="text-[10px]">virtual</Badge>}
      </div>
    </div>
    {action && <div className="shrink-0 self-end sm:self-auto">{action}</div>}
  </div>
);

export default function YarnShopPage() {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  // Orphan PDF pagination + search-attach state
  const [orphans, setOrphans] = useState({ items: [], total: 0, offset: 0, limit: 25 });
  const [orphanLoading, setOrphanLoading] = useState(false);
  const [linkingId, setLinkingId] = useState(null);
  const [searchPdfId, setSearchPdfId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  // Bulk-virtual fix state
  const [fixingVirtual, setFixingVirtual] = useState(false);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }),
    []
  );

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/yarnshop/audit`, { headers });
      setAudit(data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Garnbutikk-modulen er ikke aktivert for denne kontoen.');
      } else {
        toast.error(err.response?.data?.detail || 'Kunne ikke hente garnbutikk-revisjon');
      }
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const loadOrphans = useCallback(
    async (offset = 0, limit = 25) => {
      setOrphanLoading(true);
      try {
        const { data } = await axios.get(
          `${API}/api/yarnshop/orphan-pdfs?offset=${offset}&limit=${limit}`,
          { headers }
        );
        setOrphans({ items: data.items, total: data.total, offset, limit });
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Kunne ikke hente foreldreløse PDF-er');
      } finally {
        setOrphanLoading(false);
      }
    },
    [headers]
  );

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (tab === 'orphans' && orphans.items.length === 0) {
      loadOrphans(0, 25);
    }
  }, [tab, orphans.items.length, loadOrphans]);

  const linkPdf = async (productId, mediaId) => {
    setLinkingId(`${productId}-${mediaId}`);
    try {
      const { data } = await axios.post(
        `${API}/api/yarnshop/link`,
        { product_id: productId, media_id: mediaId },
        { headers }
      );
      if (data.skipped) {
        toast.info('PDF allerede koblet');
      } else {
        toast.success(`Koblet PDF til produkt${data.virtual_set ? ' (virtual satt)' : ''}`);
      }
      setSearchPdfId(null);
      setSearchQuery('');
      setSearchResults([]);
      await Promise.all([reload(), loadOrphans(orphans.offset, orphans.limit)]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kobling feilet');
    } finally {
      setLinkingId(null);
    }
  };

  const searchProducts = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const { data } = await axios.get(
        `${API}/api/products?search=${encodeURIComponent(query)}&limit=10`,
        { headers }
      );
      const items = data.products || data.items || (Array.isArray(data) ? data : []);
      setSearchResults(items);
    } catch {
      setSearchResults([]);
    }
  };

  const fixAllVirtual = async () => {
    if (!audit?.virtual_missing?.length) return;
    setFixingVirtual(true);
    try {
      const ids = audit.virtual_missing.map((p) => p.id);
      const { data } = await axios.post(
        `${API}/api/yarnshop/fix-virtual-bulk`,
        { product_ids: ids },
        { headers, timeout: 300000 }
      );
      toast.success(
        `Oppdaterte ${data.updated} produkter` +
          (data.failed ? ` · ${data.failed} feilet` : '') +
          (data.skipped ? ` · ${data.skipped} hoppet over` : '')
      );
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk-oppdatering feilet');
    } finally {
      setFixingVirtual(false);
    }
  };

  if (loading && !audit) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Laster garnbutikk-revisjon...</p>
      </div>
    );
  }
  if (!audit) return null;

  const summary = audit.summary || {};

  return (
    <div className="p-0 sm:p-2 md:p-0 space-y-6 max-w-7xl mx-auto" data-testid="yarnshop-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold flex items-center gap-2">
            <FilePdf size={24} weight="bold" className="sm:hidden" />
            <FilePdf size={28} weight="bold" className="hidden sm:inline" />
            Garnbutikk
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            Oversikt over PDF-mønstre, foreldreløse filer og felter som ikke er rettet for nedlasting.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={reload}
          disabled={loading}
          data-testid="yarn-refresh-btn"
        >
          {loading ? 'Oppdaterer...' : 'Oppdater'}
        </Button>
      </header>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <SummaryCard
          label="Mangler PDF"
          value={summary.pattern_missing_pdf}
          intent={summary.pattern_missing_pdf > 0 ? 'danger' : 'good'}
          icon={Warning}
          hint="Oppskrift/PDF-kategori uten fil"
        />
        <SummaryCard
          label="Nedlastbar uten fil"
          value={summary.downloadable_missing_pdf}
          intent={summary.downloadable_missing_pdf > 0 ? 'danger' : 'good'}
          icon={Warning}
          hint="downloadable=true men downloads tom"
        />
        <SummaryCard
          label="Mangler virtuell"
          value={summary.pattern_missing_virtual}
          intent={summary.pattern_missing_virtual > 0 ? 'warn' : 'good'}
          icon={Sparkle}
          hint="Oppskrift med PDF som ikke er virtuell"
        />
        <SummaryCard
          label="Foreldreløse PDF-er"
          value={summary.orphan_pdfs}
          intent={summary.orphan_pdfs > 0 ? 'warn' : 'good'}
          icon={FilePdf}
          hint={`av ${fmt(summary.total_pdfs)} PDF-er totalt`}
        />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        {/* Horizontal scroll wrapper so the 5 Norwegian tab labels (with counts)
            don't overflow on narrow phones. */}
        <div className="overflow-x-auto -mx-1 px-1 pb-1 [-webkit-overflow-scrolling:touch]">
          <TabsList className="w-max" data-testid="yarn-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Oversikt</TabsTrigger>
          <TabsTrigger value="missing" data-testid="tab-missing">
            Mangler PDF ({fmt(summary.pattern_missing_pdf)})
          </TabsTrigger>
          <TabsTrigger value="downloadable" data-testid="tab-downloadable">
            Nedlastbar uten fil ({fmt(summary.downloadable_missing_pdf)})
          </TabsTrigger>
          <TabsTrigger value="virtual" data-testid="tab-virtual">
            Mangler virtuell ({fmt(summary.pattern_missing_virtual)})
          </TabsTrigger>
          <TabsTrigger value="orphans" data-testid="tab-orphans">
            Foreldreløse PDF-er ({fmt(summary.orphan_pdfs)})
          </TabsTrigger>
        </TabsList>
        </div>

        <TabsContent value="overview">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Hva sjekker vi?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p>
                <Badge variant="destructive" className="mr-2">Regel 1</Badge>
                Produkter i kategoriene <em>PDF, oppskrift, hekle, mønster</em> må ha en PDF-fil i <code>downloads</code>.
              </p>
              <p>
                <Badge variant="destructive" className="mr-2">Regel 2</Badge>
                Produkter med <code>downloadable: true</code> må ha en PDF-fil i <code>downloads</code>.
              </p>
              <p>
                <Badge variant="secondary" className="mr-2">Regel 3</Badge>
                Oppskrifter med PDF (ikke strikkepakker) må ha <code>virtual: true</code>.
              </p>
              <p>
                <Badge variant="outline" className="mr-2">Bonus</Badge>
                PDF-er i mediebiblioteket uten tilkobling til et produkt — vi viser
                forslag basert på filnavn-likhet og <code>wp_parent</code>.
              </p>
              <p className="text-xs text-muted-foreground pt-2 border-t">
                PDF-er slettes aldri automatisk. Mediescanneren beskytter alle <code>application/pdf</code>-filer.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="missing">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Oppskrift-/PDF-kategori uten PDF ({fmt(audit.pattern_missing?.length || 0)} viser, max 200)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {audit.pattern_missing?.length ? (
                <div className="divide-y" data-testid="missing-pdf-list">
                  {audit.pattern_missing.map((p) => (
                    <ProductRow
                      key={p.id}
                      product={p}
                      action={
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setTab('orphans');
                            setSearchPdfId(null);
                          }}
                        >
                          Finn PDF
                        </Button>
                      }
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  Alle oppskrifter har PDF. 🎉
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="downloadable">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Nedlastbar uten fil ({fmt(audit.downloadable_missing?.length || 0)})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {audit.downloadable_missing?.length ? (
                <div className="divide-y" data-testid="downloadable-missing-list">
                  {audit.downloadable_missing.map((p) => (
                    <ProductRow key={p.id} product={p} action={null} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  Ingen nedlastbare uten fil.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="virtual">
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
              <CardTitle className="text-base">
                Oppskrifter med PDF som ikke er virtuelle ({fmt(audit.virtual_missing?.length || 0)})
              </CardTitle>
              {audit.virtual_missing?.length > 0 && (
                <Button
                  size="sm"
                  className="gap-2 self-start sm:self-auto"
                  onClick={fixAllVirtual}
                  disabled={fixingVirtual}
                  data-testid="fix-virtual-bulk-btn"
                >
                  <MagicWand size={14} weight="bold" />
                  <span className="hidden sm:inline">{fixingVirtual ? 'Oppdaterer...' : `Sett virtual=true på alle (${audit.virtual_missing.length})`}</span>
                  <span className="sm:hidden">{fixingVirtual ? 'Oppdaterer...' : `Fiks alle (${audit.virtual_missing.length})`}</span>
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {audit.virtual_missing?.length ? (
                <div className="divide-y" data-testid="virtual-missing-list">
                  {audit.virtual_missing.map((p) => (
                    <ProductRow key={p.id} product={p} action={null} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  Alle oppskrifter med PDF er virtuelle. 🎉
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="orphans">
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
              <CardTitle className="text-base">
                Foreldreløse PDF-er ({fmt(orphans.total)})
              </CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={orphans.offset === 0 || orphanLoading}
                  onClick={() => loadOrphans(Math.max(0, orphans.offset - orphans.limit), orphans.limit)}
                >
                  Forrige
                </Button>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {orphans.offset + 1}–{Math.min(orphans.offset + orphans.limit, orphans.total)} / {fmt(orphans.total)}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={orphans.offset + orphans.limit >= orphans.total || orphanLoading}
                  onClick={() => loadOrphans(orphans.offset + orphans.limit, orphans.limit)}
                >
                  Neste
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {orphanLoading ? (
                <p className="text-sm text-muted-foreground py-6 text-center">Laster...</p>
              ) : orphans.items.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  Ingen foreldreløse PDF-er. 🎉
                </p>
              ) : (
                <div className="space-y-3" data-testid="orphan-pdfs-list">
                  {orphans.items.map((pdf) => (
                    <div
                      key={pdf.id}
                      className="border border-border rounded p-3"
                      data-testid={`orphan-pdf-${pdf.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <FilePdf size={14} className="text-muted-foreground" />
                            <span className="font-medium truncate" title={pdf.title}>
                              {pdf.title || pdf.filename || `PDF #${pdf.id}`}
                            </span>
                            <a
                              href={pdf.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-muted-foreground hover:text-primary"
                            >
                              <ArrowSquareOut size={12} />
                            </a>
                          </div>
                          <p className="text-xs text-muted-foreground truncate" title={pdf.source_url}>
                            {pdf.source_url}
                          </p>
                        </div>
                      </div>

                      {pdf.suggestions && pdf.suggestions.length > 0 && (
                        <div className="mt-3 pl-6 space-y-1">
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                            Foreslåtte produkter:
                          </p>
                          {pdf.suggestions.map((s) => (
                            <div
                              key={s.product_id}
                              className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 sm:gap-2 text-sm"
                            >
                              <span className="truncate min-w-0">
                                <span className="font-medium">{s.name}</span>
                                <span className="text-xs text-muted-foreground ml-2">
                                  ({Math.round(s.score * 100)}% match)
                                  {s.already_has_pdf && ' · har allerede PDF'}
                                </span>
                              </span>
                              <Button
                                size="sm"
                                variant="default"
                                className="gap-1 self-end sm:self-auto shrink-0"
                                disabled={linkingId === `${s.product_id}-${pdf.id}`}
                                onClick={() => linkPdf(s.product_id, pdf.id)}
                                data-testid={`link-suggestion-${pdf.id}-${s.product_id}`}
                              >
                                <Plug size={12} weight="bold" />
                                Koble
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Manual search */}
                      <div className="mt-3 pl-6">
                        {searchPdfId === pdf.id ? (
                          <div className="space-y-2">
                            <div className="flex gap-2">
                              <Input
                                placeholder="Søk etter produkt..."
                                value={searchQuery}
                                onChange={(e) => {
                                  setSearchQuery(e.target.value);
                                  searchProducts(e.target.value);
                                }}
                                data-testid={`search-input-${pdf.id}`}
                                autoFocus
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSearchPdfId(null);
                                  setSearchQuery('');
                                  setSearchResults([]);
                                }}
                              >
                                Avbryt
                              </Button>
                            </div>
                            {searchResults.length > 0 && (
                              <div className="border border-border rounded divide-y max-h-60 overflow-y-auto">
                                {searchResults.map((p) => (
                                  <div
                                    key={p.id}
                                    className="flex items-center justify-between px-2 py-1.5 text-sm"
                                  >
                                    <span className="truncate">{p.name}</span>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => linkPdf(p.id, pdf.id)}
                                      disabled={linkingId === `${p.id}-${pdf.id}`}
                                    >
                                      <Plug size={12} weight="bold" /> Koble
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-xs gap-1"
                            onClick={() => {
                              setSearchPdfId(pdf.id);
                              setSearchQuery('');
                              setSearchResults([]);
                            }}
                            data-testid={`open-search-${pdf.id}`}
                          >
                            <MagnifyingGlass size={12} /> Søk og koble manuelt
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
